import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Check, X, ExternalLink, Sparkles } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { CuratorBar } from "@/components/admin/CuratorBar";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSignals = () => {
  const [signals, setSignals] = useState([]);

  const load = () => adminApi("/admin/signals").then(setSignals).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await adminApi(`/admin/signals/${id}/${action}`, { method: "POST" });
      toast.success(action === "approve" ? "Approved — AI draft created, cover image generating" : "Signal rejected");
      load();
    } catch (e) { toast.error(e.message); }
  };
  const approve = (id) => act(id, "approve");
  const reject = (id) => act(id, "reject");

  const newSignals = signals.filter((s) => s.status === "new");
  const processed = signals.filter((s) => s.status !== "new");

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-1" data-testid="signals-inbox-heading">Signals Inbox</h1>
        <p className="text-sm text-[var(--text-secondary)]">
          Review curated intelligence signals. Approve to create an AI draft with a generated cover, or reject.
        </p>
      </div>

      <CuratorBar onFinished={load} />

      {newSignals.length === 0 ? (
        <div className="bg-[var(--bg-card)] border-glow rounded-xl p-12 text-center">
          <p className="text-[var(--text-secondary)] mb-2">No new signals</p>
          <p className="text-xs text-[var(--text-secondary)]/60">
            Click "Find new signals" or wait for the next scheduled run. External tools can also send signals via the webhook API.
          </p>
          <div className="mt-6 bg-[var(--bg-secondary)] rounded-lg p-4 text-left max-w-lg mx-auto">
            <p className="text-xs text-[var(--text-secondary)] mb-2">Webhook endpoint:</p>
            <code className="text-xs text-[var(--accent-violet-light)] break-all">POST {API}/api/admin/signals/ingest</code>
            <p className="text-xs text-[var(--text-secondary)] mt-2">Requires the <span className="text-white">X-API-Key</span> header. <Link to="/admin/automation" className="text-[var(--accent-violet-light)]" data-testid="signals-automation-link">View key &amp; setup</Link></p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {newSignals.map((s) => (
            <div key={s._id} className="bg-[var(--bg-card)] border-glow rounded-xl p-6" data-testid={`signal-${s._id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {s.category && <span className="category-badge bg-violet-500/20 text-violet-300">{s.category}</span>}
                    {s.relevance_score > 0 && <span className="text-xs font-semibold text-amber-300" data-testid={`signal-score-${s._id}`}>Score: {s.relevance_score}/10</span>}
                    {s.origin === "ai_curator" && <span className="text-[10px] uppercase tracking-wider text-[var(--accent-violet-light)] flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Curated</span>}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">{s.title}</h3>
                  {s.suggested_headline && <p className="text-sm text-[var(--accent-violet-light)] mb-2">Suggested: {s.suggested_headline}</p>}
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-3">{s.summary}</p>
                  {s.adaptive_intelligence_relevance && (
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-3"><span className="text-white font-medium">Why it matters: </span>{s.adaptive_intelligence_relevance}</p>
                  )}
                  {s.suggested_angle && <p className="text-xs text-[var(--text-secondary)] italic mb-3" data-testid={`signal-angle-${s._id}`}>Angle: {s.suggested_angle}</p>}
                  <div className="flex items-center gap-4 text-xs text-[var(--text-secondary)]">
                    {s.source && <span>Source: {s.source}</span>}
                    {s.source_url && <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[var(--accent-violet-light)]"><ExternalLink className="w-3 h-3" /> View</a>}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => approve(s._id)} className="flex items-center gap-1.5 bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 px-4 py-2 rounded-lg text-sm font-medium transition-all" data-testid={`approve-${s._id}`}>
                    <Check className="w-4 h-4" /> Approve
                  </button>
                  <button onClick={() => reject(s._id)} className="flex items-center gap-1.5 bg-red-600/20 text-red-400 hover:bg-red-600/30 px-4 py-2 rounded-lg text-sm font-medium transition-all" data-testid={`reject-${s._id}`}>
                    <X className="w-4 h-4" /> Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {processed.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-semibold text-white mb-4">Processed</h2>
          <div className="space-y-2">
            {processed.map((s) => (
              <div key={s._id} className="bg-[var(--bg-card)]/50 border-glow rounded-lg p-4 flex items-center justify-between" data-testid={`processed-signal-${s._id}`}>
                <div className="opacity-70">
                  <p className="text-sm text-white">{s.title}</p>
                  <p className="text-xs text-[var(--text-secondary)]">{s.source}</p>
                </div>
                {s.article_id && <Link to={`/admin/articles/${s.article_id}/edit`} className="ml-auto mr-4 text-xs text-[var(--accent-violet-light)]" data-testid={`open-draft-${s._id}`}>Open draft</Link>}
                <span className={`text-xs px-2 py-0.5 rounded-full ${s.status === "approved" ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
                  {s.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSignals;
